import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from "../../src/config";
import { toast } from "react-toastify";

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    password: "",
    userType: "user",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // ======================================================
  // INLINE VALIDATION RULES
  // ======================================================
  const validators = {
    firstname: (v) =>
      /^[A-Za-z]+$/.test(v) ? "" : "First name must contain only alphabets.",
    lastname: (v) =>
      /^[A-Za-z]+$/.test(v) ? "" : "Last name must contain only alphabets.",
    email: (v) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(v)) return "Invalid email format.";

      if (v.includes("@gmail.com")) {
        const prefix = v.split("@gmail.com")[0];
        if (prefix.length < 4)
          return "Email before @gmail.com must be at least 4 characters.";
      }
      return "";
    },
    phone: (v) =>
      /^\d{9,15}$/.test(v)
        ? ""
        : "Phone number must be between 9 and 15 digits.",

    password: (v) =>
      /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#]).{8,}$/.test(v)
        ? ""
        : "Password must be 8+ chars, include uppercase, number & special character.",
  };

  // ======================================================
  // HANDLE INPUT CHANGE + INLINE VALIDATION
  // ======================================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    setErrors((prev) => ({
      ...prev,
      [name]: validators[name] ? validators[name](value) : "",
    }));
  };

  // ======================================================
  // FINAL VALIDATION BEFORE SUBMIT
  // ======================================================
  const validateForm = () => {
    let newErrors = {};

    Object.keys(formData).forEach((key) => {
      if (validators[key]) {
        const error = validators[key](formData[key]);
        if (error) newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ======================================================
  // HANDLE NORMAL FORM SUBMIT
  // ======================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      //. FIX: check data.success instead of response.ok
      if (data.success) {
        toast.success("Registration successful! Please login.");
        setFormData({
          firstname: "",
          lastname: "",
          email: "",
          phone: "",
          password: "",
          userType: "user",
        });
        setErrors({});
      } else if (data.code === "EMAIL_EXISTS") {
        // This email may belong to an account auto-created during a guest
        // checkout, which never had a password set — a flat "already
        // exists" error leaves the user stuck with no way forward, so
        // point them at login and forgot-password instead.
        toast.error(
          <span>
            An account with this email already exists.{" "}
            <Link to="/login" style={{ textDecoration: "underline" }}>
              Log in
            </Link>{" "}
            or{" "}
            <Link
              to="/forgotpassword"
              state={{ email: formData.email }}
              style={{ textDecoration: "underline" }}
            >
              set/reset your password
            </Link>
            .
          </span>,
          { autoClose: 10000 }
        );
      } else {
        toast.error(data.message || "User already exists");
      }
    } catch (error) {
      toast.error(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  // ======================================================
  // GOOGLE LOGIN HANDLER
  // ======================================================
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await fetch(`${API_BASE_URL}auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: credentialResponse.credential,
          userType: formData.userType, // 👈 THIS IS THE FIX
        }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("user", JSON.stringify(data.data));
        localStorage.setItem("accessToken", data.data.accessToken);
        localStorage.setItem("refreshToken", data.data.refreshToken);
        navigate("/");
      } else {
        toast.error(data.message || "Google login failed");
      }
    } catch (error) {
      toast.error(`Google login error: ${error.message}`);
    }
  };


  // ======================================================
  // RENDER
  // ======================================================
  return (
    <section className="logincontainer">
      <div className="container">
        <div className="row justify-content-center">
          {/* LEFT BOX */}
          <div className="col-xxl-5 col-xl-4 col-lg-6 col-md-5 col-sm-12 col-12 align-self-center">
            <div className="logintextbox">
              <div className="loginheading">WELCOME BACK!</div>
              <p>To keep connected with us please login with your personal info.</p>
              <Link to="/login" className="theme-btn signupbtn">
                Sign In
              </Link>
            </div>
          </div>

          {/* RIGHT BOX */}
          <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-7 col-sm-12 col-12 align-self-center">
            <div className="loginbox">
              <div className="loginheading">Register</div>
              <p>Create your account to access all features.</p>

              <form onSubmit={handleSubmit}>
                <div className="row gx-2">

                  {/* SIGNUP AS TABS */}
                  <div className="col-12">
                    <div className="form-group">
                      <label className="form-label mb-2 d-block" style={{fontSize: "16px", textAlign: "center"}}>Sign up as</label>
                      <div className="signupusbtx" style={{width: "100%"}}>
                        {["user", "owner"].map((type) => (
                          <button
                            style={{ width: "100%" }}
                            key={type}
                            type="button"
                            className={`signuptab ${formData.userType === type ? "active" : "inactive"
                              }`}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                userType: type,
                              }))
                            }
                          >
                            {type === "user" ? "Guest" : "Property Owner"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* FIRST NAME */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <div className="formgroupinner">
                        <i className="fa fa-user-o"></i>
                        <input
                          type="text"
                          name="firstname"
                          value={formData.firstname}
                          onChange={handleChange}
                          className={`form-control ${errors.firstname ? "is-invalid" : "is-valid"
                            }`}
                          placeholder="First Name"
                          required
                        />
                      </div>
                      {errors.firstname && (
                        <small className="text-danger">{errors.firstname}</small>
                      )}
                    </div>
                  </div>

                  {/* LAST NAME */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <div className="formgroupinner">
                        <i className="fa fa-user-o"></i>
                        <input
                          type="text"
                          name="lastname"
                          value={formData.lastname}
                          onChange={handleChange}
                          className={`form-control ${errors.lastname ? "is-invalid" : "is-valid"
                            }`}
                          placeholder="Last Name"
                          required
                        />
                      </div>
                      {errors.lastname && (
                        <small className="text-danger">{errors.lastname}</small>
                      )}
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="col-12">
                    <div className="form-group">
                      <div className="formgroupinner">
                        <i className="fa fa-envelope-o"></i>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`form-control ${errors.email ? "is-invalid" : "is-valid"
                            }`}
                          placeholder="Email ID"
                          required
                        />
                      </div>
                      {errors.email && (
                        <small className="text-danger">{errors.email}</small>
                      )}
                    </div>
                  </div>

                  {/* PHONE */}
                  <div className="col-12">
                    <div className="form-group">
                      <div className="formgroupinner">
                        <i className="icon icon-phone"></i>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`form-control ${errors.phone ? "is-invalid" : "is-valid"
                            }`}
                          placeholder="Phone Number"
                          required
                        />
                      </div>
                      {errors.phone && (
                        <small className="text-danger">{errors.phone}</small>
                      )}
                    </div>
                  </div>

                  {/* PASSWORD */}
                  <div className="col-12">
                    <div className="form-group">
                      <div className="formgroupinner" style={{ position: "relative" }}>
                        {/* <i className="fa fa-key"></i> */}

                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className={`form-control ${errors.password ? "is-invalid" : "is-valid"
                            }`}
                          placeholder="Password"
                          required
                        />

                        {/* EYE ICON */}
                        <i
                          className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: "18px",
                            color: "#666"
                          }}
                        ></i>
                      </div>

                      {errors.password && (
                        <small className="text-danger">{errors.password}</small>
                      )}
                    </div>
                  </div>

                </div>

                <div className="d-flex justify-content-center">
                  <button
                    className="theme-btn loginbtn signupbtn"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Signing Up..." : "Sign Up"}
                  </button>
                </div>
              </form>

              {/* GOOGLE BUTTON */}
              <div className="googlebtn mt-4">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error("Google Signup Failed")}
                  theme="filled"
                  shape="pill"
                  text="signin_with"
                  width="260"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterForm;
